/**
 * helpCMS.js — CMS entity schemas and data layer for Help/Support.
 *
 * E1 spec entities:
 * - article: title, anchors, blocks (text/image/video/callout), status (draft|published)
 * - release_note: date, title, body, announce (bool), optional file attachment
 * - info_banner: severity, schedule (starts/ends), dismissible, active toggle
 * - context_map: page → articles (schema only, no UI)
 *
 * Workflow buckets: Customers, Quotes, Jobs & WO, Calendar, Invoicing, Settings & Onboarding
 */

// ─── Entity Schemas ──────────────────────────────────────────────────────────

/**
 * @typedef {object} Article
 * @property {string} id
 * @property {string} title
 * @property {string} bucketId - Workflow bucket this belongs to
 * @property {string} status - 'draft' | 'published'
 * @property {Array<{id: string, label: string}>} anchors - Section anchors for outline
 * @property {Array<ArticleBlock>} blocks - Content blocks
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {object} ArticleBlock
 * @property {string} type - 'text' | 'image' | 'video' | 'callout'
 * @property {string} content - Text content or URL
 * @property {string} [caption] - For images/videos
 * @property {string} [calloutType] - 'info' | 'warning' | 'tip'
 */

/**
 * @typedef {object} ReleaseNote
 * @property {string} id
 * @property {string} date - ISO date
 * @property {string} title
 * @property {string} body - Markdown or HTML
 * @property {boolean} announce - If true, shows as login banner ONCE per user
 * @property {string|null} attachmentUrl - Optional file attachment
 * @property {string} createdAt
 */

/**
 * @typedef {object} InfoBanner
 * @property {string} id
 * @property {string} severity - 'info' | 'warning' | 'error'
 * @property {string} message - Banner text
 * @property {string|null} scheduleStart - ISO datetime (null = immediate)
 * @property {string|null} scheduleEnd - ISO datetime (null = indefinite)
 * @property {boolean} dismissible
 * @property {boolean} active
 * @property {string} createdAt
 */

// ─── Workflow Buckets ────────────────────────────────────────────────────────

export const HELP_BUCKETS = [
  { id: 'customers', label: 'Customers', icon: 'users', description: 'Managing customer records and contacts' },
  { id: 'quotes', label: 'Quotes', icon: 'clipboard-list', description: 'Creating and sending quotes' },
  { id: 'jobs_wo', label: 'Jobs & WO', icon: 'wrench', description: 'Job management and work orders' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar', description: 'Scheduling and calendar views' },
  { id: 'invoicing', label: 'Invoicing', icon: 'banknotes', description: 'Creating and managing invoices' },
  { id: 'settings', label: 'Settings & Onboarding', icon: 'cog', description: 'Account setup and configuration' },
];

// ─── Seed Articles ───────────────────────────────────────────────────────────

const SEED_ARTICLES = [
  {
    id: 'art-cust-01', title: 'Adding and Managing Customers', bucketId: 'customers', status: 'published',
    anchors: [{ id: 'add', label: 'Adding a Customer' }, { id: 'edit', label: 'Editing Details' }, { id: 'contacts', label: 'Managing Contacts' }],
    blocks: [
      { type: 'text', content: 'Learn how to add new customers, manage their contact information, and track their project history.' },
      { type: 'callout', content: 'Tip: Use the quick-add drawer from any page by pressing the + button in the sidebar.' },
    ],
  },
  {
    id: 'art-cust-02', title: 'Customer Types and Statuses', bucketId: 'customers', status: 'published',
    anchors: [{ id: 'types', label: 'Customer Types' }, { id: 'statuses', label: 'Lead vs Active' }],
    blocks: [{ type: 'text', content: 'Understand the difference between leads and active customers, and how to convert between them.' }],
  },
  {
    id: 'art-quote-01', title: 'Creating a Quote', bucketId: 'quotes', status: 'published',
    anchors: [{ id: 'new', label: 'New Quote' }, { id: 'items', label: 'Adding Line Items' }, { id: 'send', label: 'Sending to Customer' }],
    blocks: [
      { type: 'text', content: 'Walk through the quote builder to create professional estimates for your customers.' },
      { type: 'callout', content: 'Pro tip: Save frequently-used items to your catalog for faster quoting.' },
    ],
  },
  {
    id: 'art-quote-02', title: 'Quote Templates and Defaults', bucketId: 'quotes', status: 'published',
    anchors: [{ id: 'templates', label: 'Using Templates' }, { id: 'defaults', label: 'Setting Defaults' }],
    blocks: [{ type: 'text', content: 'Set up quote templates and default terms to speed up your workflow.' }],
  },
  {
    id: 'art-jobs-01', title: 'Job Lifecycle Overview', bucketId: 'jobs_wo', status: 'published',
    anchors: [{ id: 'create', label: 'Creating Jobs' }, { id: 'phases', label: 'Job Phases' }, { id: 'complete', label: 'Completing a Job' }],
    blocks: [{ type: 'text', content: 'Understand how jobs flow from quote acceptance through completion and invoicing.' }],
  },
  {
    id: 'art-jobs-02', title: 'Work Orders and Scheduling', bucketId: 'jobs_wo', status: 'published',
    anchors: [{ id: 'create-wo', label: 'Creating Work Orders' }, { id: 'assign', label: 'Assigning Crews' }, { id: 'track', label: 'Time Tracking' }],
    blocks: [
      { type: 'text', content: 'Break jobs into work orders, assign them to crews, and track time spent on each task.' },
      { type: 'callout', content: 'Work orders appear on the calendar once a date is assigned.' },
    ],
  },
  {
    id: 'art-cal-01', title: 'Calendar Views and Navigation', bucketId: 'calendar', status: 'published',
    anchors: [{ id: 'views', label: 'Month/Week/Day Views' }, { id: 'filters', label: 'Filtering by Crew' }],
    blocks: [{ type: 'text', content: 'Navigate between month, week, and day views. Filter by crew to see specific schedules.' }],
  },
  {
    id: 'art-cal-02', title: 'Scheduling Work Orders', bucketId: 'calendar', status: 'published',
    anchors: [{ id: 'drag', label: 'Drag to Schedule' }, { id: 'conflicts', label: 'Conflict Detection' }],
    blocks: [{ type: 'text', content: 'Drag work orders onto the calendar to schedule them. The system will warn you about conflicts.' }],
  },
  {
    id: 'art-inv-01', title: 'Creating and Sending Invoices', bucketId: 'invoicing', status: 'published',
    anchors: [{ id: 'create', label: 'Creating Invoices' }, { id: 'send', label: 'Sending to Customer' }, { id: 'track', label: 'Payment Tracking' }],
    blocks: [{ type: 'text', content: 'Generate invoices from completed jobs, send them to customers, and track payment status.' }],
  },
  {
    id: 'art-set-01', title: 'Company Settings and Onboarding', bucketId: 'settings', status: 'published',
    anchors: [{ id: 'profile', label: 'Company Profile' }, { id: 'team', label: 'Team Management' }, { id: 'branding', label: 'Branding' }],
    blocks: [{ type: 'text', content: 'Configure your company profile, manage team members, and customize your branding.' }],
  },
  {
    id: 'art-set-02', title: 'Vocabulary and Catalog Setup', bucketId: 'settings', status: 'published',
    anchors: [{ id: 'vocab', label: 'Custom Vocabulary' }, { id: 'catalog', label: 'Item Catalog' }],
    blocks: [{ type: 'text', content: 'Customize dropdown options and build your item catalog for faster quoting.' }],
  },
  {
    id: 'art-set-03', title: 'Integrations and Payments', bucketId: 'settings', status: 'published',
    anchors: [{ id: 'stripe', label: 'Stripe Setup' }, { id: 'quickbooks', label: 'QuickBooks' }],
    blocks: [{ type: 'text', content: 'Connect payment processors and accounting software to streamline your billing.' }],
  },
];

// ─── API Helpers ─────────────────────────────────────────────────────────────

export const helpCMSAPI = {
  /**
   * Get all published articles, optionally filtered by bucket.
   */
  async getArticles(bucketId = null) {
    const published = SEED_ARTICLES.filter(a => a.status === 'published');
    if (!bucketId) return published;
    return published.filter(a => a.bucketId === bucketId);
  },

  /**
   * Get a single article by ID.
   */
  async getArticle(id) {
    return SEED_ARTICLES.find(a => a.id === id) || null;
  },

  /**
   * Search articles by query (titles + body + video transcripts).
   */
  async searchArticles(query) {
    if (!query || !query.trim()) return [];
    const q = query.toLowerCase();
    return SEED_ARTICLES.filter(a =>
      a.status === 'published' && (
        a.title.toLowerCase().includes(q) ||
        a.blocks.some(b => b.content.toLowerCase().includes(q))
      )
    );
  },

  /**
   * Get all release notes (newest first).
   */
  async getReleaseNotes() {
    return [];
  },

  /**
   * Get the active info banner (most recent active wins — only ONE at a time).
   */
  async getActiveBanner() {
    return null;
  },

  /**
   * Check if user has dismissed a specific banner/release note.
   */
  isDismissed(id) {
    try {
      const dismissed = JSON.parse(localStorage.getItem('bidit_dismissed') || '[]');
      return dismissed.includes(id);
    } catch {
      return false;
    }
  },

  /**
   * Mark a banner/release note as dismissed for this user.
   * Persists per-user across reload.
   */
  dismiss(id) {
    try {
      const dismissed = JSON.parse(localStorage.getItem('bidit_dismissed') || '[]');
      if (!dismissed.includes(id)) {
        dismissed.push(id);
        localStorage.setItem('bidit_dismissed', JSON.stringify(dismissed));
      }
    } catch {
      // Ignore storage errors
    }
  },
};
